import React, { useState, useEffect } from 'react';
import { X, Sparkles, Gift, Check } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'trouvetonvie-promo-seen';
const VISITS_KEY = 'trouvetonvie-pages-visited';
// Déclencheurs produit : soit un visiteur qui navigue sans jamais aller sur Offres+
// (après quelques pages, on lui montre l'offre), soit un visiteur qui va sur Offres+
// directement (signal d'intérêt fort) — on le laisse d'abord découvrir les 3
// premières offres avant de teaser l'abonnement.
const PAGE_VISITS_THRESHOLD = 5;
const OFFERS_PLUS_DELAY_MS = 5500;

const FEATURES = [
    'Offres VIE exclusives hors Business France',
    'CV Matching IA avec vos offres',
    'Alertes dès qu\'une offre correspond à votre profil',
    'Accès aux archives d\'offres expirées',
];

const PromoModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (user) return;
        if (sessionStorage.getItem(STORAGE_KEY)) return;

        // Compteur de pages visitées cette session (un changement de route, pas un
        // simple changement de filtre dans l'URL)
        const visits = Number(sessionStorage.getItem(VISITS_KEY) || 0) + 1;
        sessionStorage.setItem(VISITS_KEY, String(visits));

        const delay = visits >= PAGE_VISITS_THRESHOLD
            ? 0
            : location.pathname === '/offres-plus'
                ? OFFERS_PLUS_DELAY_MS
                : null;

        if (delay === null) return;

        const timer = setTimeout(() => setIsOpen(true), delay);
        return () => clearTimeout(timer);
    }, [location.pathname, user]);

    const handleClose = () => {
        sessionStorage.setItem(STORAGE_KEY, 'true');
        setIsOpen(false);
    };

    const handleSignup = () => {
        handleClose();
        navigate('/onboarding');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            <div className="relative bg-white rounded-[32px] w-full max-w-md p-8 shadow-2xl">

                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                >
                    <X size={20} />
                </button>

                {/* Badge early adopter */}
                <div className="flex justify-center mb-6">
                    <div className="flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-full text-sm font-black border border-violet-200">
                        <Gift size={15} />
                        Offre early adopter
                    </div>
                </div>

                {/* Titre */}
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-black text-slate-900 mb-3 leading-tight">
                        Trouve<span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">+</span> est gratuit<br />pour les premiers inscrits
                    </h2>
                    <p className="text-slate-500 font-medium text-sm leading-relaxed">
                        Inscris-toi maintenant pour accéder à toutes les fonctionnalités premium — sans carte bancaire.
                    </p>
                </div>

                {/* Liste fonctionnalités */}
                <div className="space-y-3 mb-8">
                    {FEATURES.map((feature, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="w-5 h-5 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Check size={11} className="text-violet-600 font-black" strokeWidth={3} />
                            </div>
                            <span className="text-sm font-medium text-slate-700">{feature}</span>
                        </div>
                    ))}
                </div>

                {/* CTA principal */}
                <button
                    onClick={handleSignup}
                    className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-blue-600 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                    <Sparkles size={17} />
                    S'inscrire gratuitement
                </button>

                <button
                    onClick={handleClose}
                    className="w-full mt-3 text-slate-400 font-medium text-sm hover:text-slate-600 py-2 transition-colors"
                >
                    Pas maintenant
                </button>
            </div>
        </div>
    );
};

export default PromoModal;
